import { Injectable } from '@angular/core';
import { Survey } from '../models/survey';
import {
  ClientDurationTimestamps,
  DurationAuditRecord,
  DurationAuditPayload,
  DurationValidationConfig,
  DurationValidationReason,
  DurationValidationResult
} from '../models/duration-validation';
import { DbService } from './db.service';
import { FirebaseService } from './firebase.service';
import { NetworkService } from './network.service';
import { StorageTTLConfig } from '../config/storage-ttl-config';

export interface DurationValidationContext {
  survey: Survey;
  payload_snapshot: any;
  reason: DurationValidationReason;
  durationSeconds: number | null;
  timestamps: ClientDurationTimestamps;
  apiDomain?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DurationValidationService {
  private syncing = false;

  constructor(
    private dbService: DbService,
    private firebaseService: FirebaseService,
    private networkService: NetworkService
  ) {
    this.networkService.status_sub.subscribe((online) => {
      if (online) this.syncPendingAudits();
    });
  }

  evaluateDuration(config: DurationValidationConfig | null | undefined, timestamps: ClientDurationTimestamps): DurationValidationResult {
    const normalized: ClientDurationTimestamps = {
      start_datetime: timestamps.start_datetime ?? null,
      end_datetime: timestamps.end_datetime ?? null,
      duration_seconds: timestamps.duration_seconds ?? null
    };

    if (!config || !config.enabled) {
      return {
        valid: true,
        durationSeconds: this.ensureDurationSeconds(normalized)
      };
    }

    if (!normalized.start_datetime || !normalized.end_datetime) {
      return { valid: false, durationSeconds: this.ensureDurationSeconds(normalized), reason: 'missing_dates' };
    }

    const durationSeconds = this.ensureDurationSeconds(normalized);
    if (durationSeconds === null) {
      return { valid: false, durationSeconds, reason: 'missing_dates' };
    }

    if (durationSeconds < 0) {
      return { valid: false, durationSeconds, reason: 'negative_duration' };
    }
    if (config.min_seconds != null && durationSeconds < config.min_seconds) {
      return { valid: false, durationSeconds, reason: 'below_min' };
    }
    if (config.max_seconds != null && durationSeconds > config.max_seconds) {
      return { valid: false, durationSeconds, reason: 'above_max' };
    }

    return { valid: true, durationSeconds };
  }

  async handleRejectedSubmission(context: DurationValidationContext): Promise<void> {
    const type = context.survey.type?.toLowerCase() ?? '';
    let sanitizedPayload: any;
    try {
      sanitizedPayload = JSON.parse(JSON.stringify(context.payload_snapshot));
    } catch {
      sanitizedPayload = context.payload_snapshot;
    }

    const payload: DurationAuditPayload = {
      survey_id: context.survey.id_survey,
      company_id: context.survey.qs_company_id,
      campaign_id: type.includes('campa') ? context.survey.id : undefined,
      sensor_id: type.includes('sensor') ? context.survey.id : undefined,
      api_domain: context.apiDomain,
      reason: context.reason,
      duration_seconds: context.durationSeconds ?? null,
      client_timestamps: context.timestamps,
      payload_snapshot: sanitizedPayload
    };

    const record: DurationAuditRecord = {
      su_survey_id: payload.survey_id,
      qs_company_id: payload.company_id,
      qs_campaign_id: payload.campaign_id,
      qs_sensor_id: payload.sensor_id,
      api_domain: payload.api_domain,
      reason: payload.reason,
      duration_seconds: payload.duration_seconds,
      client_timestamps: payload.client_timestamps,
      payload_snapshot: payload.payload_snapshot,
      created_at: Date.now(),
      synced: 0,
      expiresAt: StorageTTLConfig.createExpirationTimestamp(StorageTTLConfig.DURATION_AUDIT_TTL)
    };

    await this.dbService.pushDurationAudit(record);

    if (this.networkService.getStatus()) {
      this.syncPendingAudits();
    }
  }

  private async syncPendingAudits() {
    if (this.syncing) return;
    this.syncing = true;
    try {
      const pending = await this.dbService.getPendingDurationAudits();
      for (const audit of pending) {
        const { id, ...auditData } = audit;
        const payload = {
          ...auditData,
          synced: 1,
          synced_at: new Date().toISOString()
        };
        const resp = await this.firebaseService.errorHandler('createDoc', payload, 'durationAuditCollection');
        if (resp != null && id != null) {
          await this.dbService.deleteDurationAudit(id);
        }
      }
    } catch (error) {
      console.warn('DurationValidationService: Error syncing audits', error);
    } finally {
      this.syncing = false;
    }
  }

  private ensureDurationSeconds(timestamps: ClientDurationTimestamps): number | null {
    if (typeof timestamps.duration_seconds === 'number') return timestamps.duration_seconds;
    if (!timestamps.start_datetime || !timestamps.end_datetime) return null;
    const start = new Date(timestamps.start_datetime).getTime();
    const end = new Date(timestamps.end_datetime).getTime();
    if (isNaN(start) || isNaN(end)) return null;
    return Math.floor((end - start) / 1000);
  }
}
